import {AsyncPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, Injector, input, output} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {combineLatestWith, defer, Observable, of} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {
    BigintDeliverableDisplayDecimalPipe
} from '../../../../components/bigint-display-decimal-pipe/bigint-deliverable-display-decimal.pipe';
import {ComputedTranche} from '../../../../components/dex/dex-streaming/types/computed/computed-tranche';
import {
    RefBalance,
    TrancheDelRefBalanceService
} from '../../../../components/dex/dex-tranche-utility/tranche-del-ref-balance.service';
import {DisplayDecimalModule} from '../../../../components/display-decimal/index';
import {DynamicZyxDaemonService} from '../../../../components/dynamic-xyz/dynamic-zyx-daemon.service';
import {PartialLoadingIndicatorModule} from '../../../../components/partial-loading-indicator/index';
import {ResponsiveColsModule} from '../../../../components/responsive-cols/responsive-cols.module';
import {Side} from '../../../../enums/side';
import {SparklineModule} from '../../../../mobile/pages/m-quotes-page/m-quotes-mini-chart/sparkline/index';
import {
    DexTrancheActionsComponent
} from '../../../dex-tranche-details-widget/dex-tranche-actions/dex-tranche-actions.component';
import {
    DexTrancheComposComponent
} from '../../../dex-tranche-details-widget/dex-tranche-compos/dex-tranche-compos.component';
import {DexTrancheSparklinePipe} from '../../../dex-tranche-details-widget/dex-tranche-sparkline.pipe';

@Component({
    selector:        'dex-tranche-details-standalone',
    templateUrl:     './dex-tranche-details-standalone.component.html',
    styleUrls:       ['./dex-tranche-details-standalone.component.scss'],
    imports:         [
        ResponsiveColsModule,
        PartialLoadingIndicatorModule,
        DisplayDecimalModule,
        BigintDeliverableDisplayDecimalPipe,
        SparklineModule,
        DexTrancheSparklinePipe,
        AsyncPipe,
        DexTrancheActionsComponent,
        DexTrancheComposComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DexTrancheDetailsStandaloneComponent {

    public readonly tranche = input.required<ComputedTranche | null>();
    public readonly isEmbedded = input<boolean>(true);
    public readonly isCollapsed = input<boolean>(false);
    public readonly isExpandCollapseEnabled = input<boolean>(true);
    public readonly isSymbolClickable = input.required<boolean>();

    public readonly toggleCollapsed = output<void>();
    public readonly symbolClick = output<string>();
    public readonly tradeClick = output<Side>();

    public readonly Side = Side;
    public readonly trancheRefBalance$ = this.createTrancheRefBalanceStream();

    private readonly dynamicZyxDaemonService = inject(DynamicZyxDaemonService);
    private readonly trancheDelRefBalanceService = inject(TrancheDelRefBalanceService);
    private readonly injector = inject(Injector);

    public onClickToggleCollapsed(event: MouseEvent): void {
        if (this.isEmbedded() && this.isExpandCollapseEnabled() && !this.getSelectionText()) {
            event.preventDefault();
            event.stopPropagation();
            this.toggleCollapsed.emit();
        }
    }

    public onSymbolClick(symbol: string, event: MouseEvent): void {
        if (this.isSymbolClickable()) {
            event.preventDefault();
            event.stopPropagation();
            this.symbolClick.emit(symbol);
        }
    }

    public onTradeClick(side: Side, event?: MouseEvent): void {
        event?.preventDefault();
        event?.stopPropagation();
        this.tradeClick.emit(side);
    }

    private getSelectionText(): string {
        return window?.getSelection()?.toString() || '';
    }

    private createTrancheRefBalanceStream(): Observable<RefBalance> {
        return defer(() => {
            const wallet$ = this.dynamicZyxDaemonService.dynRemote.primaryWallet$;
            const tr$ = toObservable(this.tranche, {injector: this.injector});

            return tr$.pipe(
                combineLatestWith(wallet$),
                switchMap(([tr, wallet]) =>
                    tr && wallet
                        ? this.trancheDelRefBalanceService.getTrancheRefBalanceStream(tr, wallet.address, this.injector)
                        : of(undefined)
                )
            );
        });
    }

}
